import React, { useState, useMemo } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { RiskBadge, StatusPill } from "./RiskBadge";
import { useNavigate } from "react-router-dom";
import { MagnifyingGlass, ArrowSquareOut, X, Table as TableIcon, Kanban, Sparkle } from "@phosphor-icons/react";

const STATUSES = ["Open", "In-Progress", "Resolved", "Closed"];
const STATUS_THEME = {
  Open: { bg: "bg-red-50", border: "border-red-200", text: "text-red-700", dot: "bg-red-500" },
  "In-Progress": { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-700", dot: "bg-amber-500" },
  Resolved: { bg: "bg-emerald-50", border: "border-emerald-200", text: "text-emerald-700", dot: "bg-emerald-500" },
  Closed: { bg: "bg-slate-50", border: "border-slate-200", text: "text-slate-600", dot: "bg-slate-400" },
};

/**
 * Wraps an observations dataset and provides:
 *  - search + filter chips (Risk, Status)
 *  - view mode toggle (Table | Kanban)
 *  - Kanban with drag-and-drop status change
 *
 * `children` receives {filtered, viewToolbar} so the wrapper can render
 * the legacy table view OR our new Kanban based on mode.
 */
export default function ObservationsToolbar({ observations, role, onStatusChange, children }) {
  const [search, setSearch] = useState("");
  const [riskFilter, setRiskFilter] = useState([]); // []|"High"|"Medium"|"Low"
  const [statusFilter, setStatusFilter] = useState([]);
  const [mode, setMode] = useState(() => localStorage.getItem("obs_view_mode") || "table");

  const setModePersist = (m) => {
    setMode(m);
    localStorage.setItem("obs_view_mode", m);
  };

  const toggle = (arr, setArr, v) =>
    arr.includes(v) ? setArr(arr.filter((x) => x !== v)) : setArr([...arr, v]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return observations.filter((o) => {
      if (q) {
        const blob = `${o.sr_no} ${o.area} ${o.observation} ${o.impact} ${o.recommendation}`.toLowerCase();
        if (!blob.includes(q)) return false;
      }
      if (riskFilter.length && !riskFilter.includes(o.risk)) return false;
      if (statusFilter.length && !statusFilter.includes(o.status)) return false;
      return true;
    });
  }, [observations, search, riskFilter, statusFilter]);

  const clearAll = () => {
    setSearch("");
    setRiskFilter([]);
    setStatusFilter([]);
  };
  const anyFilter = !!(search || riskFilter.length || statusFilter.length);

  const FilterChip = ({ active, onClick, children, tone, testid }) => (
    <button
      onClick={onClick}
      className={`text-xs px-2.5 py-1 rounded-full border transition-colors ${
        active
          ? `${tone || "bg-[#0B2A5B] border-[#0B2A5B] text-white"}`
          : "bg-white border-slate-200 text-slate-600 hover:bg-slate-50"
      }`}
      data-testid={testid}
    >
      {children}
    </button>
  );

  return (
    <div data-testid="observations-toolbar-wrapper">
      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2 mb-3 fade-up">
        <div className="relative flex-1 min-w-[200px]">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search observations…"
            className="pl-8 h-9"
            data-testid="obs-search-input"
          />
        </div>

        <div className="flex items-center gap-1.5">
          {["High", "Medium", "Low"].map((r) => (
            <FilterChip
              key={r}
              active={riskFilter.includes(r)}
              onClick={() => toggle(riskFilter, setRiskFilter, r)}
              tone={
                r === "High"
                  ? "bg-red-600 border-red-600 text-white"
                  : r === "Medium"
                  ? "bg-amber-500 border-amber-500 text-white"
                  : "bg-emerald-600 border-emerald-600 text-white"
              }
              testid={`filter-risk-${r}`}
            >
              {r}
            </FilterChip>
          ))}
        </div>
        <div className="w-px h-5 bg-slate-200 mx-1" />
        <div className="flex items-center gap-1.5">
          {STATUSES.map((s) => (
            <FilterChip
              key={s}
              active={statusFilter.includes(s)}
              onClick={() => toggle(statusFilter, setStatusFilter, s)}
              testid={`filter-status-${s}`}
            >
              {s}
            </FilterChip>
          ))}
        </div>

        {anyFilter && (
          <button
            onClick={clearAll}
            className="text-xs text-slate-500 hover:text-slate-900 inline-flex items-center gap-1 px-2"
            data-testid="clear-filters-btn"
          >
            <X size={12} /> Clear ({filtered.length})
          </button>
        )}

        <div className="ml-auto inline-flex border border-slate-200 rounded-md overflow-hidden bg-white">
          <button
            onClick={() => setModePersist("table")}
            className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${
              mode === "table" ? "bg-[#0B2A5B] text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="view-mode-table"
          >
            <TableIcon size={13} /> Table
          </button>
          <button
            onClick={() => setModePersist("kanban")}
            className={`px-3 py-1.5 text-xs flex items-center gap-1.5 ${
              mode === "kanban" ? "bg-[#0B2A5B] text-white" : "text-slate-600 hover:bg-slate-50"
            }`}
            data-testid="view-mode-kanban"
          >
            <Kanban size={13} /> Kanban
          </button>
        </div>
      </div>

      {mode === "table" ? (
        children(filtered)
      ) : (
        <KanbanView
          observations={filtered}
          role={role}
          onStatusChange={onStatusChange}
        />
      )}
    </div>
  );
}

function KanbanView({ observations, role, onStatusChange }) {
  const navigate = useNavigate();
  const [dragging, setDragging] = useState(null);
  const [hoverCol, setHoverCol] = useState(null);

  const byStatus = useMemo(() => {
    const buckets = { Open: [], "In-Progress": [], Resolved: [], Closed: [] };
    observations.forEach((o) => buckets[o.status]?.push(o));
    return buckets;
  }, [observations]);

  const onDrop = async (e, status) => {
    e.preventDefault();
    setHoverCol(null);
    if (!dragging || dragging.status === status) {
      setDragging(null);
      return;
    }
    if (role !== "audit_team") {
      toast.error("Only audit team can change status");
      setDragging(null);
      return;
    }
    try {
      await api.put(`/observations/${dragging.id}`, { status });
      toast.success(`Moved to ${status}`);
      onStatusChange?.();
    } catch (err) {
      toast.error("Failed to update status");
    } finally {
      setDragging(null);
    }
  };

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-3" data-testid="kanban-board">
      {STATUSES.map((s) => {
        const t = STATUS_THEME[s];
        const list = byStatus[s] || [];
        const isHover = hoverCol === s;
        return (
          <div
            key={s}
            onDragOver={(e) => {
              if (role !== "audit_team") return;
              e.preventDefault();
              setHoverCol(s);
            }}
            onDragLeave={() => setHoverCol(null)}
            onDrop={(e) => onDrop(e, s)}
            className={`rounded-lg border ${t.border} ${t.bg} p-2 min-h-[320px] transition-all ${
              isHover ? "ring-2 ring-[#0B2A5B] scale-[1.01]" : ""
            }`}
            data-testid={`kanban-col-${s}`}
          >
            <div className="flex items-center justify-between px-2 py-1.5 mb-2">
              <div className="flex items-center gap-2">
                <span className={`w-2 h-2 rounded-full ${t.dot}`} />
                <span className={`text-xs font-semibold uppercase tracking-wider ${t.text}`}>{s}</span>
              </div>
              <span className={`text-[10px] px-1.5 py-0.5 rounded ${t.text} bg-white border ${t.border} font-semibold`}>
                {list.length}
              </span>
            </div>

            {list.length === 0 && (
              <div className="text-[11px] text-center text-slate-400 py-4 px-2 border border-dashed border-slate-200 rounded m-1">
                {role === "audit_team" ? "Drop here" : "Empty"}
              </div>
            )}

            <div className="space-y-1.5">
              {list.map((o) => (
                <div
                  key={o.id}
                  draggable={role === "audit_team"}
                  onDragStart={() => setDragging(o)}
                  onDragEnd={() => setDragging(null)}
                  onClick={() =>
                    navigate(
                      `${role === "audit_team" ? "/audit" : "/client"}/observations/${o.id}`
                    )
                  }
                  className={`bg-white border border-slate-200 rounded-md p-2.5 cursor-pointer hover:border-[#0B2A5B] hover:shadow-sm transition-all ${
                    dragging?.id === o.id ? "opacity-40" : ""
                  } ${role === "audit_team" ? "cursor-grab active:cursor-grabbing" : ""}`}
                  data-testid={`kanban-card-${o.id}`}
                >
                  <div className="flex items-start justify-between gap-2 mb-1">
                    <span className="text-[10px] text-slate-500 font-mono">#{o.sr_no}</span>
                    <RiskBadge risk={o.risk} />
                  </div>
                  <div className="text-xs font-semibold text-slate-800 mb-1">{o.area}</div>
                  <p className="text-[11px] text-slate-600 line-clamp-3 leading-snug">
                    {o.observation}
                  </p>
                  {o.rolled_forward_from && (
                    <div className="text-[9px] uppercase tracking-wider text-[#0B2A5B] mt-1.5 font-semibold">
                      ⟳ Rolled forward
                    </div>
                  )}
                  <div className="flex items-center gap-1 mt-2 text-[10px] text-slate-400">
                    <Sparkle size={9} /> wt {o.ai_risk_weight}
                    <span className="ml-auto text-blue-600">
                      <ArrowSquareOut size={11} />
                    </span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        );
      })}
    </div>
  );
}
